import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import {
  Accordion,
  DateRangeField,
  Heading,
  ModalControlModalProps,
  NakedButton,
  Tray,
  useForm,
} from '@frontend/design-system';
import { CustomAccordionHeader } from '../../../views/appointment-list-filter-button/filter-drawer/custom-accordion-header';
import { FilterableCheckboxList } from '../../filterable-checkbox-list';
import type { ActivitySourceType, ActivityType, StatusType } from '../types';
import { AppliedFilters } from './applied-filters';
import { DEFAULT_FILTERS } from './constants';
import { hasActiveFilters, normalizeFilters, toFormValues } from './helpers';
import type { FilterOptions, FilterState } from './types';

export type FiltersFormConfig = {
  activityTypes: { type: 'checklist'; value: ActivityType[] };
  appointmentTypeIds: { type: 'checklist'; value: string[] };
  sourceTypes: { type: 'checklist'; value: ActivitySourceType[] };
  statusTypes: { type: 'checklist'; value: StatusType[] };
  dateRange: { type: 'dateRange'; value: [string, string] };
};

interface FiltersDrawerProps extends ModalControlModalProps {
  filters: FilterState;
  filterOptions: FilterOptions;
  setFilters: Dispatch<SetStateAction<FilterState>>;
  setShowEnabledFiltersBadge?: (show: boolean) => void;
}

export const FiltersDrawer = ({
  filters,
  filterOptions,
  setFilters,
  setShowEnabledFiltersBadge,
  ...modalProps
}: FiltersDrawerProps) => {
  const { t } = useTranslation('schedule-activity');

  const { getFieldProps, seedValues, values } = useForm<FiltersFormConfig, keyof FiltersFormConfig>({
    fields: {
      activityTypes: { type: 'checklist', value: [...filters.activityTypes] },
      appointmentTypeIds: { type: 'checklist', value: [...filters.appointmentTypeIds] },
      sourceTypes: { type: 'checklist', value: [...filters.sourceTypes] },
      statusTypes: { type: 'checklist', value: [...filters.statusTypes] },
      dateRange: { type: 'dateRange', value: [filters.startDate, filters.endDate] },
    },
  });

  const normalizedValues = normalizeFilters(values);

  const filterCounts = useMemo(
    () => ({
      activityTypes: normalizedValues.activityTypes.length,
      appointmentTypeIds: normalizedValues.appointmentTypeIds.length,
      sourceTypes: normalizedValues.sourceTypes.length,
      statusTypes: normalizedValues.statusTypes.length,
    }),
    [normalizedValues]
  );

  // CHECK
  const handleRemoveAppliedFilter = (type: string, value: string) => {
    switch (type) {
      case 'activityTypes':
        getFieldProps('activityTypes').onChange({ name: 'activityTypes', value: [value] });
        break;
      case 'appointmentTypeIds':
        getFieldProps('appointmentTypeIds').onChange({ name: 'appointmentTypeIds', value: [value] });
        break;
      case 'sourceTypes':
        getFieldProps('sourceTypes').onChange({ name: 'sourceTypes', value: [value] });
        break;
      case 'statusTypes':
        getFieldProps('statusTypes').onChange({ name: 'statusTypes', value: [value] });
        break;
      case 'startDate': {
        const [, endDate] = values.dateRange || ['', ''];
        seedValues({ dateRange: ['', endDate] });
        break;
      }
      case 'endDate': {
        const [startDate] = values.dateRange || ['', ''];
        seedValues({ dateRange: [startDate, ''] });
        break;
      }
    }
  };

  const handleReset = () => {
    seedValues(toFormValues(DEFAULT_FILTERS));
    setFilters(DEFAULT_FILTERS);
    setShowEnabledFiltersBadge?.(false);
  };

  const handleApply = () => {
    setFilters(normalizedValues);
    setShowEnabledFiltersBadge?.(hasActiveFilters(normalizedValues));
    modalProps.onClose?.();
  };

  useEffect(() => {
    if (!modalProps.show) return;

    seedValues(toFormValues(DEFAULT_FILTERS));
    seedValues(toFormValues(filters));
  }, [filters, modalProps.show, seedValues]);

  return (
    <Tray {...modalProps} width='smallXXL'>
      <Tray.Header
        Buttons={
          <NakedButton onClick={modalProps.onClose}>
            <Icon name='x' size={24} />
          </NakedButton>
        }
      >
        {t('Filters')}
      </Tray.Header>
      <Tray.Body>
        <Accordion variant='main' shouldRenderBody chevronSize={16}>
          <Accordion.Item value='activityType'>
            <CustomAccordionHeader title={t('Event')} filterCount={filterCounts.activityTypes} />
            <Accordion.Body>
              <FilterableCheckboxList
                listStyle={listStyle}
                fieldName='activityTypes'
                checkListField={getFieldProps('activityTypes')}
                list={filterOptions.activityTypes}
              />
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item value='appointmentType'>
            <CustomAccordionHeader title={t('Appointment Type')} filterCount={filterCounts.appointmentTypeIds} />
            <Accordion.Body>
              <FilterableCheckboxList
                listStyle={listStyle}
                fieldName='appointmentTypeIds'
                checkListField={getFieldProps('appointmentTypeIds')}
                list={filterOptions.appointmentTypes}
              />
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item value='sourceType'>
            <CustomAccordionHeader title={t('Source')} filterCount={filterCounts.sourceTypes} />
            <Accordion.Body>
              <FilterableCheckboxList
                listStyle={listStyle}
                fieldName='sourceTypes'
                checkListField={getFieldProps('sourceTypes')}
                list={filterOptions.sourceTypes}
              />
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item value='statusType'>
            <CustomAccordionHeader title={t('Status')} filterCount={filterCounts.statusTypes} />
            <Accordion.Body>
              <FilterableCheckboxList
                listStyle={listStyle}
                fieldName='statusTypes'
                checkListField={getFieldProps('statusTypes')}
                list={filterOptions.statusTypes}
              />
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
        <section
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--fabric-space-gap-sm)',
          }}
        >
          <Heading level={3}>{t('Time Frame')}</Heading>
          <DateRangeField {...getFieldProps('dateRange')} label={t('Activity Date')} />
        </section>
      </Tray.Body>
      <AppliedFilters
        filters={normalizedValues}
        filterOptions={filterOptions}
        onRemoveFilter={handleRemoveAppliedFilter}
      />
      <Tray.Actions
        primaryLabel={t('Apply')}
        secondaryLabel={t('Reset')}
        onPrimaryClick={handleApply}
        onSecondaryClick={handleReset}
      />
    </Tray>
  );
};

const listStyle = css({
  minHeight: 100,
  maxHeight: 'calc(100vh - 645px)',
  overflowY: 'auto',
});
